// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { navigate } from "@reach/router";
import { Button, Divider, Form, message, Modal, Popconfirm } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { BroadcastListWriteRequestDTO } from "../../../../api/backend-openapi";
import { formItemBlockLayout } from "../../../../utils/Constant";
import { SaveOutlined, StopOutlined } from "@ant-design/icons/lib";
import { displayError } from "../../../../utils/displayError";
import BroadcastListSettingsFormSection from "../details/BroadcastListSettingsFormSection";
import { requestCreateBroadcastList } from "../../../../state/broadcastLists/BroadcastListsEffects";
import { ValidatorResult } from "../../../types/Types";
import { validate } from "../../../../validator/BroadcastListValidator";

const { confirm } = Modal;

const AddBroadcastListForm: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<JSX.Element | undefined>(
    undefined
  );

  const confirmationPopup = async () => {
    return new Promise<boolean>(resolve => {
      confirm({
        title: t("global.confirm"),
        content: t("add_broadcast_list.modal.description"),
        okText: t("global.yes"),
        cancelText: t("global.no"),
        centered: true,
        onOk() {
          resolve(true);
        },
        onCancel() {
          resolve(false);
        }
      });
    });
  };

  useEffect(() => {
    return requestCreateBroadcastList.done.watch(({ result }) => {
      setButtonLoading(false);
      if (result.ok && result.data) {
        navigate(`/broadcastLists/${result.data.id}`);
        message.success(t("add_broadcast_list.success"));
      } else {
        if (
          result.errorCode &&
          ["other_branch", "unknown_email"].includes(result.errorCode)
        ) {
          const emails = result.errorMessage?.split(",");
          setEmailError(
            <p className="text-red mb-0">
              {t(`broadcast_list.emails.errors.${result.errorCode}`)}
              <ul>
                {emails?.map(email => (
                  <li key={email}>{email}</li>
                ))}
              </ul>
            </p>
          );
        }
        displayError(
          (result.errorCode === "wrong_user_branch"
            ? result.errorMessage
            : t("broadcast_list.emails.errors.generic")) ?? "Error!",
          false
        );
      }
    });
  });

  const handleSubmit = (values: any) => {
    const validationResult: ValidatorResult = validate(values.emails);
    if (!validationResult.validate) {
      displayError(validationResult.message);
    } else {
      confirmationPopup().then((confirmed: boolean) => {
        if (confirmed) {
          const broadcastListToAdd: BroadcastListWriteRequestDTO = {
            name: values.name,
            branch: values.branch,
            emails: values.emails
          };
          requestCreateBroadcastList(broadcastListToAdd);
          setButtonLoading(true);
        }
      });
    }
  };

  const handleSubmitToValidation = () => {
    form
      .validateFields()
      .then(values => {
        handleSubmit(values);
      })
      .catch(() => {
        displayError("global.on_finish_failed");
      });
  };

  const onFinishFailed = ({ errorFields }: any) => {
    displayError("global.on_finish_failed");
    form.scrollToField(errorFields[0].name);
  };

  return (
    <Form
      {...formItemBlockLayout}
      onFinish={handleSubmitToValidation}
      onFinishFailed={onFinishFailed}
      form={form}
    >
      <div className="form-first-item-sticky-top-container">
        <div className="form-first-item-sticky-top-content">
          <Divider orientation="left">
            <h3 className="text-secondary mb-0">
              {t("broadcast_list.actions")}
            </h3>
          </Divider>
          <Form.Item className="text-center mb-0">
            <Popconfirm
              title={t("add_broadcast_list.cancel_confirmation")}
              okText={t("global.yes")}
              cancelText={t("global.no")}
              onConfirm={() => {
                navigate("/broadcastLists");
                form.resetFields();
              }}
              placement="top"
              icon={<QuestionCircleOutlined />}
            >
              <Button htmlType="reset" className="m-2">
                <StopOutlined /> {t("add_broadcast_list.cancel")}
              </Button>
            </Popconfirm>
            <Button
              className="btn-primary m-2"
              onClick={handleSubmitToValidation}
              loading={buttonLoading}
            >
              <SaveOutlined /> {t("global.save")}
            </Button>
          </Form.Item>
        </div>
      </div>
      <Divider orientation="left">
        <h3 className="text-secondary mb-0">{t("broadcast_list.settings")}</h3>
      </Divider>
      <BroadcastListSettingsFormSection
        showRequired={true}
        editMode={true}
        form={form}
        emailError={emailError}
      />
    </Form>
  );
};

export default AddBroadcastListForm;
